import React from 'react'
import { useDrop } from 'react-dnd'
import PracticeTile from './PracticeTile'
import TileDropZone from './TileDropZone'
import { TileType } from './TilePalette'
import usePracticeStore from '../../stores/practiceStore'
import { Plus, Calendar, FileText } from 'lucide-react'

const PracticeSession = () => {
  const { tiles, addTile, addTileAt, updateTile, deleteTile, notes, setNotes, sessionName, setSessionName, reorderTile } = usePracticeStore()

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'practice-tile',
    drop: (item, monitor) => {
      // Only handle drops if not handled by a child drop zone
      if (monitor.didDrop()) {
        return
      }
      
      if (item.tileId) {
        // Tile is being moved/reordered - should be handled by TileDropZone
        return
      }
      
      // Create new tile at the end (fallback for empty session)
      const newTile = {
        id: Date.now().toString(),
        type: item.tileType,
        title: getDefaultTitle(item.tileType),
        description: '',
        duration: 10 // Default 10 minutes duration
      }
      addTile(newTile)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  }))

  const getDefaultTitle = (type) => {
    switch (type) {
      case TileType.DRILL:
        return 'New Drill'
      case TileType.SCRIMMAGE:
        return 'New Scrimmage'
      case TileType.SPEAKING_POINTS:
        return 'Speaking Points'
      case TileType.FITNESS:
        return 'New Fitness'
      default:
        return 'New Activity'
    }
  }

  const handleAddNewTile = (tileType, targetIndex) => {
    const newTile = {
      id: Date.now().toString(),
      type: tileType,
      title: getDefaultTitle(tileType),
      description: '',
      duration: 10 // Default 10 minutes duration
    }
    addTileAt(newTile, targetIndex)
  }

  const sortedTiles = [...tiles].sort((a, b) => (a.startTime || 0) - (b.startTime || 0))

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-2">
          <Calendar size={20} className="text-gray-600 flex-shrink-0" />
          <input
            type="text"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            className="flex-1 min-w-0 text-2xl font-bold text-gray-900 border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2"
            placeholder="Practice Session Title"
          />
        </div>
        <p className="text-sm text-gray-600">Drag tiles from the left to add activities to your practice</p>
      </div>

      <div
        ref={drop}
        className={`min-h-[400px] p-6 rounded-lg border-2 border-dashed transition-colors ${
          isOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-gray-50'
        }`}
      >
        {sortedTiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <Plus size={48} className="mb-4" />
            <p className="text-lg font-medium">No activities yet</p>
            <p className="text-sm">Drag tiles from the left to get started</p>
          </div>
        ) : (
          <div className="space-y-0">
            {sortedTiles.map((tile, index) => (
              <React.Fragment key={tile.id}>
                <TileDropZone 
                  index={index} 
                  onReorder={(draggedTileId, targetIndex) => {
                    reorderTile(draggedTileId, targetIndex)
                  }}
                  onAddNew={handleAddNewTile}
                />
                <div className="py-1.5">
                  <PracticeTile
                    tile={tile}
                    onUpdate={updateTile}
                    onDelete={deleteTile}
                  />
                </div>
              </React.Fragment>
            ))}
            <TileDropZone 
              index={sortedTiles.length} 
              onReorder={(draggedTileId, targetIndex) => {
                reorderTile(draggedTileId, targetIndex)
              }}
              onAddNew={handleAddNewTile}
            />
          </div>
        )}
      </div>

      {/* Notes Section */}
      <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-3">
          <FileText size={18} className="text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[120px]"
          placeholder="Add any notes, reminders, or additional information about this practice session..."
        />
      </div>

      {/* Practice Summary */}
      {sortedTiles.length > 0 && (() => {
        // Calculate total duration by tile type
        const durationByType = sortedTiles.reduce((acc, tile) => {
          const type = tile.type || 'unknown'
          const duration = tile.duration || 10
          acc[type] = (acc[type] || 0) + duration
          return acc
        }, {})

        // Get type labels
        const typeLabels = {
          [TileType.DRILL]: 'Drills',
          [TileType.SCRIMMAGE]: 'Scrimmages',
          [TileType.SPEAKING_POINTS]: 'Speaking Points',
          [TileType.FITNESS]: 'Fitness'
        }

        const totalDuration = sortedTiles.length > 0 
          ? (sortedTiles[sortedTiles.length - 1].endTime || 0) + 1
          : 0

        return (
          <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Practice Summary</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Total Activities:</span>
                <span>{sortedTiles.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Total Duration:</span>
                <span>{totalDuration} minutes</span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="font-medium mb-2">Duration by Type:</div>
                <div className="space-y-1">
                  {Object.entries(durationByType)
                    .sort(([a], [b]) => {
                      // Sort by type order: drill, scrimmage, speaking-points, fitness
                      const order = [TileType.DRILL, TileType.SCRIMMAGE, TileType.SPEAKING_POINTS, TileType.FITNESS]
                      return order.indexOf(a) - order.indexOf(b)
                    })
                    .map(([type, duration]) => (
                      <div key={type} className="flex justify-between">
                        <span>{typeLabels[type] || type}:</span>
                        <span>{duration} minutes</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

export default PracticeSession

